/**
 * Rewrites the catalog id segment in a catalog-scoped pathname so the user
 * stays on the same tab (profile / findings / table) when switching
 * catalogs via the sidebar picker. Returns the input unchanged for
 * non-catalog-scoped paths (`/`, `/changes`, `/catalog`, `/welcome`, etc.) so
 * the caller can detect a no-op and skip the navigate() call.
 *
 * Matches `/catalogs/<anything-without-slashes>(/<rest>)?` so it's safe
 * against nested routes the team might add later (e.g. /catalogs/:id/findings/:findingId).
 *
 * Uses a replacer FUNCTION (not a template-literal replacement string) so
 * a `$`-prefixed substring in `nextId` doesn't get interpreted as a
 * regex backreference (`$1`, `$&`, etc.). UUIDs make this dormant today
 * but the function form costs nothing and removes the trap entirely.
 */
export function rewriteCatalogIdInPath(pathname: string, nextId: string): string {
  return pathname.replace(
    /^\/catalogs\/[^/]+(\/.*)?$/,
    (_match, rest: string | undefined) => `/catalogs/${nextId}${rest ?? ''}`,
  );
}

/** Whether `pathname` is inside one catalog (`/catalogs/<id>` or below) — the
 *  routes {@link rewriteCatalogIdInPath} can carry over to another catalog. */
export function isCatalogScopedPath(pathname: string): boolean {
  return /^\/catalogs\/[^/]+(\/.*)?$/.test(pathname);
}
